import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Preview as PreviewIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Percent as PercentIcon,
  LocalOffer as DiscountIcon,
  Calculate as CalculateIcon,
  Article as PeppolIcon,
  MonetizationOn as MoneyIcon,
  Comment as CommentIcon,
  Notes as NotesIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify'; // ✅ Add this import
import DiscountDialog from './DiscountDialog';
import VATDialog from './VATDialog';

const PAYMENT_METHODS = [
  'CASH',
  'BANCONTACT',
  'VIREMENT'
];

// ✅ Helper functions to safely get item price and quantity
const getItemPrice = (item) => {
  return parseFloat(item.Price || item.SalePrice || 0);
};

const getItemQuantity = (item) => {
  return parseInt(item.Quantity || 1);
};

function CartPanel({
  cart = [],
  customer,
  selectedPaymentMethod,
  paidAmount,
  receiptNumber,
  discount,
  customVATRate,
  vatRates = [],
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onSelectCustomer,
  onPaymentMethodChange,
  onPaidAmountChange,
  onPreview,
  onPrint,
  onApplyDiscount,
  onRemoveDiscount,
  onApplyVAT,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandPayment, setExpandPayment] = React.useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = React.useState(false);
  const [vatDialogOpen, setVATDialogOpen] = React.useState(false);
  const [paidAmountError, setPaidAmountError] = React.useState(false);

  // ✅ FIXED: Calculate totals with proper rounding
  const subtotal = Math.round(cart.reduce((sum, item) => sum + (item.Quantity * item.Price), 0) * 100) / 100;
  
  const discountAmount = discount?.amount || 0;
  const totalAfterDiscount = Math.round((subtotal - discountAmount) * 100) / 100;
  
  const totalVAT = Math.round(cart.reduce((sum, item) => {
    const itemTotal = item.Quantity * item.Price;
    const itemRatio = subtotal > 0 ? itemTotal / subtotal : 0;
    const itemDiscountedTotal = itemTotal - (discountAmount * itemRatio);
    const vatRate = customVATRate !== null && customVATRate !== undefined ? customVATRate : (item.VAT || 0);
    return sum + ((itemDiscountedTotal * vatRate) / 100);
  }, 0) * 100) / 100;
  
  // ✅ FIXED: Round total to 2 decimals to avoid floating point precision issues
  const totalIncl = Math.round((totalAfterDiscount + totalVAT) * 100) / 100;
  
  // ✅ FIXED: Round change calculation
  const change = Math.round(((parseFloat(paidAmount) || 0) - totalIncl) * 100) / 100;

  // Action button handlers
  const handleDiscount = () => {
    setDiscountDialogOpen(true); // ✅ Open discount dialog
  };

  const handleVAT = () => {
    setVATDialogOpen(true);
  };

  const handlePeppol = () => {
    // TODO: Implement Peppol integration
    console.log('Peppol clicked');
  };

  const handleRounding = () => {
    // TODO: Implement rounding options
    console.log('Rounding clicked');
  };

  const handleComments = () => {
    // TODO: Implement comments/notes dialog
    console.log('Comments/Notes clicked');
  };

  // ✅ Modified handlePrint to validate with proper comparison
  const handlePrintClick = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }

    // ✅ FIXED: Compare with tolerance for floating point errors
    if (selectedPaymentMethod === 'CASH' && (!paidAmount || parseFloat(paidAmount) < totalIncl - 0.01)) {
      setPaidAmountError(true);
      setExpandPayment(true);
      toast.error(t('please_enter_amount_paid') || 'Please enter amount paid');
      return;
    }

    setPaidAmountError(false);
    onPrint && onPrint();
  };

  // ✅ Modified handlePreview to validate with proper comparison
  const handlePreviewClick = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }

    // ✅ FIXED: Compare with tolerance for floating point errors
    if (selectedPaymentMethod === 'CASH' && (!paidAmount || parseFloat(paidAmount) < totalIncl - 0.01)) {
      setPaidAmountError(true);
      setExpandPayment(true);
      toast.error(t('please_enter_amount_paid') || 'Please enter amount paid');
      return;
    }

    setPaidAmountError(false);
    onPreview && onPreview();
  };

  // ✅ Clear error when user starts typing
  const handlePaidAmountChange = (value) => {
    setPaidAmountError(false);
    onPaidAmountChange && onPaidAmountChange(value);
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: { xs: '100%', md: 380, lg: 400 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f8f9fa',
          borderLeft: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon sx={{ fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {t('current_sale') || 'Current Sale'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={cart.length}
              size="small"
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 800,
                fontSize: '0.85rem',
                minWidth: 28,
                height: 28,
              }}
            />
            <IconButton
              size="small"
              onClick={onClearCart}
              disabled={cart.length === 0}
              sx={{
                color: 'white',
                bgcolor: alpha('#fff', 0.15),
                '&:hover': {
                  bgcolor: alpha('#fff', 0.25),
                },
                '&:disabled': {
                  color: alpha('#fff', 0.3),
                  bgcolor: alpha('#fff', 0.08),
                },
              }}
            >
              <ClearIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Customer Selection */}
        <Box sx={{ p: 1.5, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PersonIcon fontSize="small" />}
            onClick={onSelectCustomer}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              borderRadius: 1.5,
              py: 1,
              fontSize: '0.85rem',
              borderColor: '#d0d0d0',
              color: customer ? 'primary.main' : 'text.secondary',
              fontWeight: customer ? 600 : 400,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha('#667eea', 0.05),
              },
            }}
          >
            {customer ? customer.Name : t('select_customer_optional') || 'Select Customer (Optional)'}
          </Button>
        </Box>

        {/* Cart Items */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            bgcolor: 'white',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: alpha('#000', 0.05),
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha('#667eea', 0.3),
              borderRadius: '3px',
              '&:hover': {
                bgcolor: alpha('#667eea', 0.5),
              },
            },
          }}
        >
          {cart.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <ReceiptIcon sx={{ fontSize: 60, color: '#d0d0d0', mb: 1.5 }} />
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                {t('cart_is_empty') || 'Cart is empty'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                {t('select_products_to_add') || 'Select products to add'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: '100%' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.75, px: 1, color: '#2c3e50' }}>
                      {t('product') || 'Product'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.75, px: 0.5, color: '#2c3e50', width: 50 }}>
                      {t('price') || 'Price'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.75, px: 0.5, color: '#2c3e50', width: 85 }}>
                      {t('qty') || 'Qty'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.75, px: 0.5, color: '#2c3e50', width: 60 }}>
                      {t('total') || 'Total'}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 32, py: 0.75, px: 0 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.map((item) => {
                    const itemPrice = getItemPrice(item);
                    const itemQuantity = getItemQuantity(item);
                    const rowKey = item._rowKey || item.Id;

                    return (
                      <TableRow
                        key={rowKey}
                        sx={{
                          '&:hover': {
                            bgcolor: alpha('#667eea', 0.03),
                          },
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <TableCell sx={{ py: 0.75, px: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              color: '#2c3e50',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.Name || 'Unknown'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 0.75, px: 0.5 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            €{itemPrice.toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={{ py: 0.75, px: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => onUpdateQuantity(rowKey, itemQuantity - 1)}
                              sx={{
                                bgcolor: '#ff5252',
                                color: 'white',
                                width: 22,
                                height: 22,
                                minWidth: 22,
                                '&:hover': { bgcolor: '#ff1744' },
                                p: 0,
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: 12 }} />
                            </IconButton>

                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#667eea', fontSize: '0.8rem', minWidth: 24, textAlign: 'center' }}>
                              {itemQuantity}
                            </Typography>

                            <IconButton
                              size="small"
                              onClick={() => onUpdateQuantity(rowKey, itemQuantity + 1)}
                              sx={{
                                bgcolor: '#4caf50',
                                color: 'white',
                                width: 22,
                                height: 22,
                                minWidth: 22,
                                '&:hover': { bgcolor: '#43a047' },
                                p: 0,
                              }}
                            >
                              <AddIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        </TableCell>

                        <TableCell align="right" sx={{ py: 0.75, px: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea', fontSize: '0.85rem' }}>
                            €{(itemPrice * itemQuantity).toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={{ py: 0.75, px: 0 }}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveItem(rowKey)}
                            sx={{
                              width: 24,
                              height: 24,
                              minWidth: 24,
                              '&:hover': { bgcolor: alpha('#f44336', 0.1) },
                              p: 0,
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Action Buttons Row */}
        <Box
          sx={{
            bgcolor: 'white',
            borderTop: '1px solid #e0e0e0',
            borderBottom: '2px solid #e0e0e0',
            px: 1,
            py: 1,
          }}
        >
          <Grid container spacing={0.75} justifyContent="center">
            {/* Discount Button */}
            <Grid item>
              <Tooltip title={t('apply_discount') || 'Apply Discount'}>
                <Box
                  onClick={handleDiscount}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 58,
                    height: 58,
                    borderRadius: 2,
                    bgcolor: alpha('#ff9800', 0.1),
                    border: '2px solid',
                    borderColor: discount ? '#ff9800' : alpha('#ff9800', 0.3),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': {
                      bgcolor: alpha('#ff9800', 0.2),
                      borderColor: '#ff9800',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)',
                    },
                  }}
                >
                  {discount && (
                    <Chip
                      label="✓"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: '#4caf50',
                        color: 'white',
                        fontWeight: 700,
                        minWidth: 20,
                        height: 20,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                  <DiscountIcon sx={{ fontSize: 22, color: '#ff9800', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#ff9800' }}>
                    {t('discount') || 'Discount'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>

            {/* ✅ VAT Button - Updated to show percentage inline */}
            <Grid item>
              <Tooltip title={t('adjust_vat') || 'Adjust VAT'}>
                <Box
                  onClick={handleVAT}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 58,
                    height: 58,
                    borderRadius: 2,
                    bgcolor: alpha('#2196f3', 0.1),
                    border: '2px solid',
                    borderColor: customVATRate !== null && customVATRate !== undefined && customVATRate !== 21 
                      ? '#2196f3' 
                      : alpha('#2196f3', 0.3),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': {
                      bgcolor: alpha('#2196f3', 0.2),
                      borderColor: '#2196f3',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(33, 150, 243, 0.3)',
                    },
                  }}
                >
                  {customVATRate !== null && customVATRate !== undefined && customVATRate !== 21 && (
                    <Chip
                      label="✓"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: '#4caf50',
                        color: 'white',
                        fontWeight: 700,
                        minWidth: 20,
                        height: 20,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                  
                  {/* ✅ Show percentage and icon on same line */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '1rem', fontWeight: 800, color: '#2196f3' }}>
                      {customVATRate !== null && customVATRate !== undefined ? customVATRate : 21}
                    </Typography>
                    <PercentIcon sx={{ fontSize: 20, color: '#2196f3' }} />
                  </Box>
                  
                  {/* ✅ VAT text below */}
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#2196f3' }}>
                    {t('vat') || 'VAT'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>

            {/* Rounding Button */}
            <Grid item>
              <Tooltip title={t('rounding') || 'Rounding'}>
                <Box
                  onClick={handleRounding}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 58,
                    height: 58,
                    borderRadius: 2,
                    bgcolor: alpha('#9c27b0', 0.1),
                    border: '2px solid',
                    borderColor: alpha('#9c27b0', 0.3),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha('#9c27b0', 0.2),
                      borderColor: '#9c27b0',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)',
                    },
                  }}
                >
                  <MoneyIcon sx={{ fontSize: 22, color: '#9c27b0', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9c27b0' }}>
                    {t('round') || 'Round'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>

            {/* Comments/Notes Button */}
            <Grid item>
              <Tooltip title={t('add_comments_notes') || 'Add Comments/Notes'}>
                <Box
                  onClick={handleComments}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 58,
                    height: 58,
                    borderRadius: 2,
                    bgcolor: alpha('#f44336', 0.1),
                    border: '2px solid',
                    borderColor: alpha('#f44336', 0.3),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha('#f44336', 0.2),
                      borderColor: '#f44336',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(244, 67, 54, 0.3)',
                    },
                  }}
                >
                  <NotesIcon sx={{ fontSize: 22, color: '#f44336', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#f44336' }}>
                    {t('notes') || 'Notes'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>

            {/* Peppol Button */}
            <Grid item>
              <Tooltip title={t('peppol') || 'Peppol'}>
                <Box
                  onClick={handlePeppol}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 58,
                    height: 58,
                    borderRadius: 2,
                    bgcolor: alpha('#4caf50', 0.1),
                    border: '2px solid',
                    borderColor: alpha('#4caf50', 0.3),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha('#4caf50', 0.2),
                      borderColor: '#4caf50',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
                    },
                  }}
                >
                  <PeppolIcon sx={{ fontSize: 22, color: '#4caf50', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#4caf50' }}>
                    {t('peppol') || 'Peppol'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* ✅ Discount Display (if applied) */}
        {discount && (
          <Box
            sx={{
              bgcolor: alpha('#ff9800', 0.1),
              borderBottom: '2px solid',
              borderColor: alpha('#ff9800', 0.3),
              p: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DiscountIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                <Box>
                  <Typography variant="caption" fontWeight={700} color="#ff9800">
                    {t('discount_applied') || 'Discount Applied'}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {discount.type === 'percentage' ? `${discount.value}%` : `€${discount.value.toFixed(2)}`}
                    {discount.reason && ` - ${discount.reason}`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} color="error.main">
                  -€{discountAmount.toFixed(2)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onRemoveDiscount}
                  sx={{
                    width: 24,
                    height: 24,
                    color: 'error.main',
                    '&:hover': { bgcolor: alpha('#f44336', 0.1) },
                  }}
                >
                  <CancelIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* ✅ Custom VAT Display (if applied and different from 21%) */}
        {customVATRate !== null && customVATRate !== undefined && customVATRate !== 21 && (
          <Box
            sx={{
              bgcolor: alpha('#2196f3', 0.1),
              borderBottom: '2px solid',
              borderColor: alpha('#2196f3', 0.3),
              p: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PercentIcon sx={{ fontSize: 18, color: '#2196f3' }} />
                <Box>
                  <Typography variant="caption" fontWeight={700} color="#2196f3">
                    {t('custom_vat_applied') || 'Custom VAT Applied'}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {customVATRate}% VAT Rate
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} color="primary.main">
                  {customVATRate}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onApplyVAT && onApplyVAT(null)}
                  sx={{
                    width: 24,
                    height: 24,
                    color: 'error.main',
                    '&:hover': { bgcolor: alpha('#f44336', 0.1) },
                  }}
                >
                  <CancelIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* Compact Totals & Payment Section */}
        <Box
          sx={{
            bgcolor: 'white',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Compact Total Summary */}
          <Box
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              cursor: 'pointer',
            }}
            onClick={() => setExpandPayment(!expandPayment)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                  {t('subtotal') || 'Subtotal'}: €{subtotal.toFixed(2)}
                  {discount && ` - Discount: €${discountAmount.toFixed(2)}`}
                  {' + '}{t('vat') || 'VAT'}: €{totalVAT.toFixed(2)}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.2rem', mt: 0.25 }}>
                  {t('total') || 'TOTAL'}: €{totalIncl.toFixed(2)}
                </Typography>
              </Box>
              <IconButton size="small" sx={{ color: 'white' }}>
                {expandPayment ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* Expandable Payment Details */}
          <Collapse in={expandPayment}>
            <Box sx={{ p: 2 }}>
              {/* Payment Method */}
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }}>{t('payment_method') || 'Payment Method'}</InputLabel>
                <Select
                  value={selectedPaymentMethod || 'CASH'}
                  label={t('payment_method') || 'Payment Method'}
                  onChange={(e) => onPaymentMethodChange && onPaymentMethodChange(e.target.value)}
                  sx={{
                    borderRadius: 1.5,
                    fontSize: '0.85rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d0d0d0',
                    },
                  }}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method} sx={{ fontSize: '0.85rem' }}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Cash Payment Details */}
              {selectedPaymentMethod === 'CASH' && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('amount_paid') || 'Amount Paid'}
                    type="number"
                    value={paidAmount || ''}
                    onChange={(e) => handlePaidAmountChange(e.target.value)}
                    error={paidAmountError} // ✅ Show error state
                    helperText={paidAmountError ? (t('amount_required') || 'Amount paid is required') : ''} // ✅ Show error message
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, fontWeight: 600, fontSize: '0.85rem' }}>€</Typography>,
                    }}
                    sx={{
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        fontSize: '0.85rem',
                        // ✅ Red border when error
                        '& fieldset': {
                          borderColor: paidAmountError ? '#f44336' : undefined,
                          borderWidth: paidAmountError ? 2 : 1,
                        },
                        '&:hover fieldset': {
                          borderColor: paidAmountError ? '#f44336' : undefined,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: paidAmountError ? '#f44336' : undefined,
                        },
                        // ✅ Red background tint when error
                        bgcolor: paidAmountError ? alpha('#f44336', 0.05) : 'white',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.85rem',
                        // ✅ Red label when error
                        color: paidAmountError ? '#f44336' : undefined,
                        '&.Mui-focused': {
                          color: paidAmountError ? '#f44336' : undefined,
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      },
                    }}
                  />

                  {paidAmount && parseFloat(paidAmount) > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 1.25,
                        bgcolor: change < 0 ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1),
                        borderRadius: 1.5,
                        border: '2px solid',
                        borderColor: change < 0 ? alpha('#f44336', 0.3) : alpha('#4caf50', 0.3),
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: change < 0 ? 'error.main' : 'success.main' }}>
                        {change < 0 ? t('insufficient') || 'Insufficient' : t('change') || 'Change'}:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem', color: change < 0 ? 'error.main' : 'success.main' }}>
                        €{Math.abs(change).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>

          {/* Action Buttons */}
          <Box sx={{ p: 2, pt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<PrintIcon fontSize="small" />}
                  onClick={handlePrintClick} // ✅ Use validation handler
                  disabled={cart.length === 0}
                  sx={{
                    py: 1.25,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                  }}
                >
                  {t('print') || 'Print'}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<VisibilityIcon />}
                  onClick={handlePreviewClick} // ✅ Use validation handler
                  disabled={cart.length === 0}
                  sx={{
                    py: 1.2,
                    borderRadius: 2,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: 'primary.50',
                    },
                    '&:disabled': {
                      borderColor: 'grey.300',
                      color: 'grey.400',
                    },
                  }}
                >
                  {t('preview') || 'Preview'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* ✅ Discount Dialog */}
      <DiscountDialog
        open={discountDialogOpen}
        onClose={() => setDiscountDialogOpen(false)}
        currentTotal={subtotal}
        onApplyDiscount={onApplyDiscount}
      />

      {/* ✅ VAT Dialog */}
      <VATDialog
        open={vatDialogOpen}
        onClose={() => setVATDialogOpen(false)}
        vatRates={vatRates}
        currentVATRate={customVATRate !== null && customVATRate !== undefined ? customVATRate : 21}
        onApplyVAT={onApplyVAT}
      />
    </>
  );
}

export default CartPanel;

// Example: If you have a filter/search in CartPanel
const safeFilter = (arr, query) => {
  query = (query || '').toLowerCase();
  return arr.filter(item =>
    (item.Name || '').toLowerCase().includes(query) ||
    (item.Reference || '').toLowerCase().includes(query) ||
    (item.Barcode || '').toLowerCase().includes(query)
  );
};

// Use safeFilter wherever you filter/search items